/**
 * Pure helpers behind the landing-page announcement bar.
 *
 * Kept free of React and of Supabase so the server page, the client bar, the
 * admin editor, and the unit tests all agree on one definition of "what is
 * valid" and "who sees it".
 */

/** Who the bar renders for. Mirrors the site_settings check constraint. */
export type AnnouncementAudience = "admins" | "everyone";

/**
 * Icons the bar can lead with. A curated key set rather than free-form icon
 * names, so the landing bundle only carries the icons we actually offer and an
 * admin can't type a key that resolves to nothing. The key→component map lives
 * in the bar component; labels for the picker live in the admin editor.
 */
export const ANNOUNCEMENT_ICON_KEYS = [
  "sparkles",
  "rocket",
  "megaphone",
  "flame",
  "zap",
  "gift",
  "party",
  "tag",
  "wrench",
  "palette",
  "calendar",
  "bell",
] as const;

export type AnnouncementIconKey = (typeof ANNOUNCEMENT_ICON_KEYS)[number];

/** A renderable announcement: enabled, with copy, and a vetted link (or none). */
export type Announcement = {
  text: string;
  /** Already passed through {@link safeAnnouncementHref}; null renders text-only. */
  href: string | null;
  audience: AnnouncementAudience;
  /** Leading icon, or null for a text-only bar. */
  icon: AnnouncementIconKey | null;
  /** Present only alongside `href`: renders the link as a pill button. */
  ctaLabel: string | null;
};

/** The raw site_settings columns, as they arrive from Supabase or the API. */
export type AnnouncementSettings = {
  announcement_enabled?: boolean | null;
  announcement_text?: string | null;
  announcement_href?: string | null;
  announcement_audience?: string | null;
  announcement_icon?: string | null;
  announcement_cta_label?: string | null;
};

/** Single localStorage slot. The stored value is the dismissed signature. */
export const ANNOUNCEMENT_DISMISS_STORAGE_KEY = "ws:landing-announcement-dismissed";

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

export function isAnnouncementAudience(value: unknown): value is AnnouncementAudience {
  return value === "admins" || value === "everyone";
}

export function isAnnouncementIconKey(value: unknown): value is AnnouncementIconKey {
  return ANNOUNCEMENT_ICON_KEYS.includes(value as AnnouncementIconKey);
}

/** Unknown/retired icon keys degrade to "no icon" rather than a broken glyph. */
export function normalizeAnnouncementIcon(value: unknown): AnnouncementIconKey | null {
  return isAnnouncementIconKey(value) ? value : null;
}

/**
 * Vet an admin-supplied link before it reaches an `href` attribute.
 *
 * Allow-list, never a deny-list: same-site anchors/paths, plus absolute http(s)
 * URLs. Everything else — `javascript:`, `data:`, `mailto:`, protocol-relative
 * `//evil.com` and its `/\evil.com` variant that browsers normalize the same
 * way — collapses to null, which renders the bar as plain text.
 *
 * This value is world-readable (site_settings has a public read policy) and is
 * rendered on the marketing page, so a hijacked admin session must not be able
 * to turn it into script execution. Applied on save AND on render.
 */
export function safeAnnouncementHref(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;

  if (value.startsWith("#")) return value;

  if (value.startsWith("/")) {
    return value.startsWith("//") || value.startsWith("/\\") ? null : value;
  }

  try {
    const url = new URL(value);
    // Reading .protocol after parsing defeats obfuscation (case, embedded tabs
    // and newlines) because the URL parser has already normalized it.
    return ALLOWED_PROTOCOLS.has(url.protocol) ? url.toString() : null;
  } catch {
    return null;
  }
}

/** True for absolute links, which open in a new tab instead of client-routing. */
export function isExternalAnnouncementHref(href: string | null): boolean {
  return !!href && /^https?:\/\//i.test(href);
}

/**
 * Turn raw settings into something renderable, or null when there is nothing to
 * show (disabled, or enabled with empty copy). Audience is preserved so the
 * viewer check can happen later, on the client, where admin status is known.
 */
export function normalizeAnnouncement(
  settings: AnnouncementSettings | null | undefined,
): Announcement | null {
  if (!settings?.announcement_enabled) return null;

  const text = (settings.announcement_text ?? "").trim();
  if (!text) return null;

  const href = safeAnnouncementHref(settings.announcement_href);
  const ctaLabel = (settings.announcement_cta_label ?? "").trim();

  return {
    text,
    href,
    audience: isAnnouncementAudience(settings.announcement_audience)
      ? settings.announcement_audience
      : "admins",
    icon: normalizeAnnouncementIcon(settings.announcement_icon),
    // A CTA label with nothing to click is meaningless — drop it rather than
    // render a button that goes nowhere.
    ctaLabel: href && ctaLabel ? ctaLabel : null,
  };
}

/** The rollout gate: everyone sees an "everyone" bar; only admins see the rest. */
export function shouldShowAnnouncement(
  announcement: Announcement | null,
  viewer: { isAdmin: boolean },
): boolean {
  if (!announcement) return false;
  return announcement.audience === "everyone" || viewer.isAdmin;
}

/**
 * Content fingerprint used to scope a dismissal.
 *
 * Covers every visible field, so changing just the icon or the button label
 * counts as a new announcement. Audience is deliberately excluded: widening who
 * can see the bar is not a content change, and folding it in would resurrect the
 * bar for admins who had already dismissed it.
 *
 * Storing the signature (rather than a bare "dismissed" flag) means editing the
 * copy or the link re-shows the bar to everyone who dismissed the previous
 * announcement — there is no version field for an admin to forget to bump.
 * Re-saving identical content keeps it dismissed.
 */
export function announcementSignature(announcement: Announcement): string {
  const { text, href, icon, ctaLabel } = announcement;
  // Space-joined with empty-string fallbacks so field boundaries stay distinct:
  // ("a b", no link) cannot collide with ("a", link "b").
  return hash32([text, href ?? "", icon ?? "", ctaLabel ?? ""].join(" "));
}

export function isAnnouncementDismissed(
  storedSignature: string | null,
  announcement: Announcement,
): boolean {
  return storedSignature === announcementSignature(announcement);
}

/** FNV-1a, base36. Not a security primitive — just a short, stable content id. */
function hash32(input: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}
